/**
 * Send a stretch of visible time on a page to `POST /usage/page-views`.
 *
 * Not a TanStack mutation and not the generated SDK: it fires as the person
 * leaves a page or hides the tab, when an ordinary `fetch` may be cancelled
 * with the page. `navigator.sendBeacon` is built for exactly that moment; a
 * `keepalive` fetch is the fallback. Both carry the session cookie (same
 * origin) and neither waits for, or can act on, the answer — the endpoint
 * always says 204, and a lost beacon is a slightly low number on an admin
 * screen, nothing more.
 *
 * Bypassing the SDK also keeps its error interceptor out of it, so a beacon
 * sent after the session expired cannot surface as a sign-in prompt. Only the
 * configured client's `baseUrl` is read, from `./client` rather than the bare
 * generated one, so the beacon goes wherever every other call goes.
 */

import { client } from "./client";
import type { PageViewRequest } from "./generated/types.gen";

const PATH = "/usage/page-views";

export function sendPageView(body: PageViewRequest): void {
  const url = `${client.getConfig().baseUrl ?? ""}${PATH}`;
  const payload = JSON.stringify(body);
  try {
    if (navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }))) {
      return;
    }
  } catch {
    // Not available, or refused: try the fallback.
  }
  void fetch(url, {
    method: "POST",
    body: payload,
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    keepalive: true,
  }).catch(() => undefined);
}
